import { CircularProgress, Tooltip } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import { activateSink, activateSource, runSource, setDetailViewCard } from 'js/actions';
import { GET_FLOW_RUN_SUMMARY_REQUEST, RUN_SOURCE_NOW_REQUEST } from 'js/constants/actionTypes';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import DateUtils from 'js/lib/DateUtils';
import { isReadOnly } from 'js/lib/ACLUtils';
import { ReactComponent as PlayIcon } from 'new/components/icons/fi-rr-play.svg';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { FLOW_TYPE_CUSTOM } from 'js/constants/pipelineConstants';
import { useFlowRunSummary } from 'js/components/pipelines/cards/components/ExpandActions/hooks/useExpandActionsEffects';
import styles from '../FlowNodes.module.scss';
import nodeStyles from './ScheduleNode.module.scss';
import clsx from 'clsx';
import { useByoCodeConfigured } from './useByoCodeConfigured';
import { getSinkNodeByFlowId } from 'js/reducers/pipeline/selectors';

interface LastRunNodeProps {
  data: {
    layoutDirection?: 'LR' | 'TB';
    nodeKey?: string;
    flow_node_id?: number;
    data_source_id?: string;
    id?: string;
    resourceId?: string;
    runtime_status?: string;
    ingestion_mode?: string;
    [key: string]: any;
  };
}

export const LastRunNode: React.FC<LastRunNodeProps> = ({ data }) => {
  const dispatch = useAppDispatch();
  const layoutDirection = data.layoutDirection ?? 'LR';
  const isTB = layoutDirection === 'TB';
  const sourceHandleId = isTB ? 'bottom' : 'right';
  // In TB the node always sits above the source and emits from its bottom
  // edge (solo: straight vertical drop; paired: shallow V converging at
  // the source top). In LR it sits to the left of the source and emits
  // from the right edge.
  const handlePos = isTB ? Position.Bottom : Position.Right;

  const flowRunSummary = useSelector((state: any) => state.metrics.flowRunSummary);

  useFlowRunSummary(RESOURCE_TYPE_SOURCE, data, flowRunSummary, true);

  const isRunNowLoading = useIsLoading(RUN_SOURCE_NOW_REQUEST);
  const isFlowRunSummaryLoading = useIsLoading(GET_FLOW_RUN_SUMMARY_REQUEST);

  const isByoGateActive = data.flow_type === FLOW_TYPE_CUSTOM && !!data.byoV2Enabled;
  // Same "code configured" signal CodeNode renders from (item/store
  // code_container_id, the ui.byo_code_configured flag, or the source-detail
  // backfill /flows needs) — otherwise pre-v2 BYO sources show a configured
  // Code node next to a disabled Run Now.
  const { codeConfigured } = useByoCodeConfigured({
    item: data.item ?? data,
    isPreview: !!data.isPreview,
    enabled: isByoGateActive,
  });

  // BYO only makes sense to run once source and code both exist — a source
  // with no sink is a no-op, and unconfigured code has nothing to execute.
  // A PAUSED source or destination is no longer a block: product decision is
  // that Run Now should activate whichever of the two is paused, then run —
  // see handleRunNow. Mirrors the same "does this flow even have a
  // destination/code" gates ByoDestShellNode/CodeNode use to render "Blocked"
  // on canvas, but for the actual Run Now trigger.
  const byoRunGate = useSelector(
    (
      state: any,
    ): {
      blocked: boolean;
      reason: string | null;
      sinkId: number | string | null;
      sourceNeedsActivation: boolean;
      sinkNeedsActivation: boolean;
    } => {
      const none = {
        blocked: false,
        reason: null,
        sinkId: null,
        sourceNeedsActivation: false,
        sinkNeedsActivation: false,
      };
      // Gated on UI_ENABLE_BYO_V2 (threaded via nodeProps → data.byoV2Enabled) —
      // with the flag off, a flow_type: 'custom' flow (which may predate this v2
      // rollout) gets none of this gate, matching the pre-v2 getScheduleCanvasVisibility
      // behavior it already falls back to (see utils.ts).
      if (!isByoGateActive) return none;
      const flowId = String(data.origin_node_id ?? data.flow_node_id ?? '');
      if (!flowId) return none;

      const sinkNode: any = getSinkNodeByFlowId(state)[flowId];
      if (!sinkNode) return { ...none, blocked: true, reason: 'no-destination' };

      const sink = state.entities?.sinks?.[sinkNode.data_sink_id];
      if (!codeConfigured) return { ...none, blocked: true, reason: 'code-not-configured' };

      return {
        ...none,
        sinkId: sinkNode.data_sink_id ?? null,
        sourceNeedsActivation: data.status === 'PAUSED',
        sinkNeedsActivation: !!sink && sink.status === 'PAUSED',
      };
    },
  );
  // isRunNowLoading only covers runSource itself, not the activate calls
  // awaited before it — without this a second click mid-activation starts a
  // second activate+run sequence.
  const runInFlight = useRef(false);
  // Run Now can activate a paused source/destination — never for view-only
  // (collaborator) users, same ACL helper CodeNode/ByoDestShellNode gate on.
  const isByoReadOnly = isByoGateActive && isReadOnly(data.item ?? data);

  // No destination at all: the whole run-now/cron launcher has nothing to attach
  // to, so hide it entirely rather than show a permanently-disabled control.
  if (byoRunGate.reason === 'no-destination') return null;

  const isSourceProcessing = data.runtime_status === 'PROCESSING';
  const isRunNowDisabled =
    isSourceProcessing ||
    isRunNowLoading ||
    data.ingestion_mode === 'sampling' ||
    byoRunGate.blocked ||
    isByoReadOnly;

  const { data: summaryData } = flowRunSummary[data.nodeKey] || {};
  const summaryRunIds = Object.keys(summaryData || {}).sort(
    (a, b) => parseInt(b, 10) - parseInt(a, 10),
  );
  const lastRunId = summaryRunIds[0];
  const formattedLastRun = lastRunId
    ? DateUtils.getDateString(new Date(parseInt(lastRunId, 10)), 'MMM Do, YYYY [at] h:mm A z')
    : 'No previous runs';

  const handleRunNow = async () => {
    if (!data?.data_source_id || runInFlight.current) return;
    // Product decision: BYO's source/destination pause state should not block
    // Run Now — activate whichever is paused first, then run. Awaited in
    // sequence (not Promise.all) so a failed activation surfaces before
    // runSource fires against a still-paused resource. Neither activateSource
    // nor activateSink catches its own rejection (confirmed by reading both
    // thunks), so a failed activate (network/API error) must be caught here —
    // otherwise it's an unhandled rejection AND runSource silently never
    // fires with no indication why. The activate thunk's own CALL_PRIVATE_API
    // failure path already surfaces a toast; this just stops the cascade.
    runInFlight.current = true;
    try {
      if (byoRunGate.sourceNeedsActivation) {
        await dispatch(activateSource(data.data_source_id));
      }
      if (byoRunGate.sinkNeedsActivation && byoRunGate.sinkId != null) {
        await dispatch(activateSink(byoRunGate.sinkId));
      }
    } catch {
      return;
    } finally {
      runInFlight.current = false;
    }
    dispatch(runSource(data.data_source_id));
  };

  const handleLastRunClick = () => {
    dispatch(setDetailViewCard(data, 'Insights'));
  };

  const runTooltip =
    (isSourceProcessing
      ? 'Unavailable - Source is currently processing'
      : data.ingestion_mode === 'sampling'
        ? 'To ingest data, please switch Sampling Mode of the source'
        : isByoReadOnly
          ? 'Unavailable - You have view-only access'
          : byoRunGate.reason === 'code-not-configured'
            ? 'Unavailable - Code processor is not configured'
            : byoRunGate.sourceNeedsActivation || byoRunGate.sinkNeedsActivation
              ? 'Will activate the paused source/destination, then run'
              : null) ?? 'Run source ingestion now';

  return (
    <div
      className={clsx(nodeStyles.scheduleNode, nodeStyles.lastRunNode)}
      data-testid="last-run-node"
    >
      <Handle
        type="source"
        position={handlePos}
        isConnectable={false}
        className={styles.handle}
        id={sourceHandleId}
      />
      <div className={nodeStyles.nodeContent}>
        <Tooltip title={runTooltip} placement="top" arrow>
          <div
            className={`${nodeStyles.iconCircle} ${nodeStyles.iconCircleRun} ${
              isRunNowDisabled ? nodeStyles.iconCircleDisabled : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isRunNowDisabled) handleRunNow();
            }}
          >
            {isRunNowLoading ? <CircularProgress size={16} color="primary" /> : <PlayIcon />}
          </div>
        </Tooltip>
        <div className={`${nodeStyles.labelBadge} ${nodeStyles.labelBadgeRun}`}>RUN NOW</div>
        <div
          className={nodeStyles.subText}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleLastRunClick();
          }}
        >
          <div>Last Run</div>
          <div>{isFlowRunSummaryLoading ? 'Loading...' : formattedLastRun}</div>
        </div>
      </div>
    </div>
  );
};
